import { AssignmentEntityInstanceResponse } from '@models/api';
import { KeyValueDescriptionType, KeyValueType } from '@models/common';
import { EITransactionInstanceModel } from '@models/module/entityInstance';
import { OrderType, SearchViewModel } from '@models/module/grid';
import { NotificationFormData } from '@module-definition/document/common';
import { ActivityDetailsModel } from '@module-instance/activity/common';
import { EntityTableRow } from '@shared/components/data-list/common';
import { DataListField } from '@shared/components/data-list/common/models/table.interface';
import { DmGridFields, DmTransitionParams } from '../../../common';
import { DocumentDetailsQueryParams, DocumentInfoModel, DocumentRelationsModel } from './document-info.model';
import { EntityInstanceSigner } from './signatures-tab.model';

type StoreLoadingType = 'loading' | 'success' | 'error';

export type DocumentDetails = {
  activityInstances: {
    instance: EITransactionInstanceModel;
    activityDetails?: ActivityDetailsModel;
  }[];
  info: DocumentInfoModel;
  assignment?: AssignmentEntityInstanceResponse;
  transitionData?: Pick<DmTransitionParams, 'role' | 'transition'> & {
    gridFields?: DmGridFields;
    formValid?: boolean;
    /* TODO: add this when adding implementation for transition dialog */
    // asignee: unknown;
    /** This is populated if !gridFields.hideRolesNotify & !autocomplete */
    notificationData?: Pick<
      NotificationFormData,
      'includeAttachments' | 'excludeRoles' | 'notify' | 'hideIncludeAttachments' | 'notifyOptions' | 'hideRolesNotify'
    >;
  };
  relations: DocumentRelationsModel[];
  signers: EntityInstanceSigner[];
  params: DocumentDetailsQueryParams;
  moduleRoles: KeyValueType<number>[];
  /** used mostly for disabling actions while loading */
  actionsDisabled: boolean;

  /* Loading states */
  loadingRollback: StoreLoadingType;
  /** `error` is what the details screen offers a retry on; the assignment call failing after the
   * information itself arrived leaves this on `success`, because the document is usable. */
  loadingGeneralInfo: StoreLoadingType;
  //   loadingRelations: StoreLoadingType;
  //   loadingSigners: StoreLoadingType;
  //   loadingActivities: StoreLoadingType;
};

/** Scoped to one document details instance: it dies with the dialog or feature that provides the store. */
export interface DocumentDetailsState {
  documents: Record<number, DocumentDetails>;
  sidebar: {
    items: EntityTableRow[];
    descriptionField: Pick<DataListField, 'restrictions' | 'formattings' | 'typeId'>;
    records: number;
    /** Incremented to ask a mounted sidebar to refetch its first page. */
    reloadToken: number;
  };
}

export const initialState: DocumentDetailsState = {
  documents: {},
  sidebar: {
    items: [],
    records: 0,
    descriptionField: null,
    reloadToken: 0,
  },
};

/** Outlives one instance: handed over across a navigation, or read by the caller after the dialog closed. */
export interface DocumentDetailsSessionState {
  startScreens: KeyValueDescriptionType<number>[];
  /** Scopes `startScreens`, which survive `clearStore` and must not be read by another module's dialog. */
  startScreensModuleId: number;
  searchViewModel?: SearchViewModel;
  dirtyDocuments: number[];
  reloadTable?: boolean;
}

export const SIDEBAR_PAGE_SIZE = 50;

export const sessionInitialState: DocumentDetailsSessionState = {
  startScreens: [],
  startScreensModuleId: null,
  searchViewModel: {
    criteria: '',
    page: 1,
    pagesize: SIDEBAR_PAGE_SIZE,
    sort: [{ field: 'full_doc_num', order: OrderType.Descending }],
  },
  dirtyDocuments: [],
  reloadTable: false,
};

export enum DocumentStoreActions {
  LoadActivities = '[Document details] Load Activities',
  LoadActivitiesSuccess = '[Document details] Load Activities Success',
  LoadActivitiesError = '[Document details] Load Activities Error',

  LoadGeneralInfo = '[Document details] Load General Info',
  LoadGeneralInfoSuccess = '[Document details] Load General Info Success',
  LoadGeneralInfoError = '[Document details] Load General Info Error',

  LoadSidebarItems = '[Document details] Load Sidebar Items Info',
  LoadSidebarItemsSuccess = '[Document details] Load Sidebar Items Info Success',
  LoadSidebarItemsError = '[Document details] Load Sidebar Items Info Error',

  LoadRelations = '[Document details] Load Relations',
  LoadRelationsSuccess = '[Document details] Load Relations Success',
  LoadRelationsError = '[Document details] Load Relations Error',

  LoadSigners = '[Document details] Load Signers',
  LoadSignersSuccess = '[Document details] Load Signers Success',
  LoadSignersError = '[Document details] Load Signers Error',

  LoadActivityInstances = '[Document details] Load Activity Instances',
  LoadActivityInstancesSuccess = '[Document details] Load Activity Instances Success',
  LoadActivityInstancesError = '[Document details] Load Activity Instances Error',

  LoadActivityDetails = '[Document details] Load Activity Details',
  LoadActivityDetailsSuccess = '[Document details] Load Activity Details Success',
  LoadActivityDetailsError = '[Document details] Load Activity Details Error',

  LoadModuleRoles = '[Document details] Load Module Roles',
  LoadModuleRolesSuccess = '[Document details] Load Module Roles Success',
  LoadModuleRolesError = '[Document details] Load Module Roles Error',

  Rollback = '[Document details] Rollback',
  RollbackSuccess = '[Document details] Rollback Success',
  RollbackError = '[Document details] Rollback Error',
  ClearLoadingRollback = '[Document details] Clear Loading Rollback',

  StartDocumentTransition = '[Document details] Start Document Transition',
  CancelDocumentTransition = '[Document details] Cancel Document Transition',
  UpdateTransitionFormData = '[Document details] Update Transition Form Data',

  LoadAssignmentSuccess = '[Document details] Load Assignment Success',

  SelectDocument = '[Document details] Select Document',
  DuplicateDocument = '[Document details] Duplicate Document',
  StartNewDocument = '[Document details] Start New Document',
  RequestSidebarReload = '[Document details] Request Sidebar Reload',
  SetActionsDisabled = '[Document details] Set Actions Disabled',
  SetSidebarItems = '[Document details] Set Sidebar Items',
  SetEntityInstanceIds = '[Document details] Set Entity Instance Ids',
  SetDocumentParams = '[Document details] Set Document Params',
  UpdateDocumentNumber = '[Document details] Update document number',
  SetEditMode = '[Document details] Set Edit Mode',
}

export enum DocumentDetailsSessionActions {
  SetSidebarSearchViewModel = '[Document details session] Set Sidebar Search ViewModel',
  SeedSidebarSearch = '[Document details session] Seed Sidebar Search',
  SetStartScreens = '[Document details session] Set Start Screens',
  SetDocumentAsDirty = '[Document details session] Set Document As Dirty',
  SetReloadTable = '[Document details session] Set Reload Table',
  ClearStore = '[Document details session] Clear Store',
  ClearDialogState = '[Document details session] Clear Dialog State',
}
